import dayjs from 'dayjs'
import { cloneDeep } from 'lodash'
import XLSX from 'xlsx'

import { DATE_FORMAT, EXCEL_LABELS, formatDate, getSumOfPrices } from '@/helpers'
import store from '@/store'
import actions from '@/store/actions'

export const prepareMilestoneApplicationsItems = (paymentMilestones, paymentApplications, currencies) => {
  return paymentMilestones.reduce((acc, curr) => {
    acc.push({ header: curr.name, key: curr.id })
    if (curr.subMilestones.length === 0) {
      const item = prepareSelectedMilestonesItem(paymentApplications, curr, currencies)
      acc.push({ ...item, disabled: item.amountToPay === 0 })
    }
    curr.subMilestones.forEach((subMilestone) => {
      const item = prepareSelectedMilestonesItem(paymentApplications, subMilestone, currencies)
      acc.push({ ...item, disabled: item.amountToPay === 0 })
    })
    return acc
  }, [])
}

export const calculatePlannedDates = (steps, applicationDate) => {
  const modifiedSteps = cloneDeep(steps)
  modifiedSteps.forEach((item, i, array) => {
    const calculationBase = item.calculationBase === null && i > 0
      ? array[i - 1]
      : item.calculationBase === null && i === 0
        ? item.calculationBase = -1
        : array.find(i => i.workflowStepId === item.calculationBase) || {}

    const baseDate = item.actualDate
      ? item.actualDate
      : item.calculationBase === -1
        ? applicationDate
        : calculationBase.actualDate || calculationBase.plannedDate

    item.plannedDate = item.actualDate || dayjs(baseDate, DATE_FORMAT).add(item.dueInDays, 'day').format(DATE_FORMAT)
  })
  return modifiedSteps.map((i, index, arr) => ({
    name: i.name,
    plannedDate: i.plannedDate,
    dueInDays: i.dueInDays,
    comment: i.comment,
    user: i.user,
    actualDate: i.actualDate || null,
    isActive: !i.actualDate && (!index || arr[index - 1].actualDate),
    workflowStepId: i.id || i.workflowStepId,
    fieldsToFill: i.fieldsToFill
  }))
}

export const prepareSelectedMilestonesItem = (paymentApplications, item, currencies) => {
  const amounts = calculateApplicationMilestoneValues(paymentApplications, item, currencies)
  const amountToPay = item.paymentAmountCents - (amounts.amountAssignedToApplication[item.currency.key] || amounts.amountAssignedToApplication.sum || 0)
  return {
    ...item,
    amountToPay,
    percentToPay: (amountToPay / item.paymentAmountCents) * 100,
    paymentPart: item.paymentPart || (item.partAmountCents / item.paymentAmountCents) * 100,
    ...amounts
  }
}

export const isApplicationFinished = (app) => {
  return app.workflowActions.length === app.workflow.workflowSteps.length
}

export const calculateDisplayDateForMilestone = (paymentApplications, milestone) => {
  if (Math.round(milestone.amountAssignedToApplication) === Math.round(milestone.paymentAmountCents)) {
    const dates = paymentApplications.filter(application => {
      return application.paymentMilestones.findIndex(i => i.id === milestone.id || i.parentMilestoneId === milestone.id) > -1
    }).map(i => {
      return i.workflowActions.length ? i.workflowActions.slice(-1)[0].actualDate : i.applicationDate
    }).sort((a, b) => new Date(a) - new Date(b))

    return dates.slice(-1)[0]
  }
  return milestone.forecastedDate
}

export const calculateApplicationMilestoneValues = (paymentApplications, milestone, currencies) => {
  const usages = paymentApplications.map(application => ({
    finished: isApplicationFinished(application),
    paymentMilestones: application.paymentMilestones.filter(i => {
      return i.id === milestone.id || i.parentMilestoneId === milestone.id
    })
  }))
  return {
    amountAssignedToApplication: getSumOfPrices(usages.map(i => i.paymentMilestones).flat(), 'partAmountCents', currencies),
    amountPaid: getSumOfPrices(usages.filter(i => i.finished).map(i => i.paymentMilestones).flat(), 'partAmountCents', currencies)
  }
}

const prepareMilestoneFromApi = (paymentApplications, milestone, currencies) => {
  const subMilestones = milestone.subMilestones
    ? milestone.subMilestones.map((subMilestone) => prepareSubMilestoneFromApi(paymentApplications, subMilestone, milestone, currencies))
    : []
  subMilestones.sort((a, b) => a.name > b.name ? 1 : -1)
  const paymentAmountCents = milestone.paymentAmountCents === null
    ? getSumOfPrices(subMilestones, 'paymentAmountCents', currencies)
    : Number(milestone.paymentAmountCents)
  const currency = milestone.partId
    ? currencies.find(i => i.key === milestone.part.currency)
    : currencies.find(i => i.value === 1)
  const newMilestone = {
    ...milestone,
    currency,
    paymentAmountCents,
    subMilestones,
    multiCurrencyMilestone: milestone.paymentAmountCents === null
  }
  return { ...newMilestone, ...calculateApplicationMilestoneValues(paymentApplications, milestone, currencies) }
}

const prepareSubMilestoneFromApi = (paymentApplications, subMilestone, milestone, currencies) => {
  const currency = subMilestone.partId
    ? currencies.find(i => i.key === subMilestone.part.currency)
    : currencies.find(i => i.value === 1)
  const newMilestone = {
    ...subMilestone,
    currency,
    paymentAmountCents: Number(subMilestone.paymentAmountCents),
    percentOfMilestone: milestone.paymentAmountCents
      ? Math.round((Number(subMilestone.paymentAmountCents) / milestone.paymentAmountCents * 100) * 100) / 100
      : null
  }
  return { ...newMilestone, ...calculateApplicationMilestoneValues(paymentApplications, subMilestone, currencies) }
}

const prepareApplicationFromApi = (application, currencies) => {
  const performedSteps = application.workflowActions.map(i => i.workflowStepId)
  const fieldsToFill = store.state.workflowsTypes.find(i => i.type === 'payment').fieldsToFill
  const steps = [
    ...application.workflowActions,
    ...application.workflow.workflowSteps
      .filter(i => !performedSteps.includes(i.id))
      .sort((a, b) => a.order - b.order)
      .map(i => ({
        ...i,
        workflowStepId: i.id,
        fieldsToFill: i.fieldsToFill.map(name => fieldsToFill.find(field => field.key === name))
      }))
  ]
  const workflowSteps = calculatePlannedDates(steps, application.applicationDate)

  return {
    ...application,
    actions: application.workflow,
    paymentAmount: application.amountCents / 100,
    paymentMilestones: application.paymentMilestones.map(milestone => {
      const currency = milestone.partId
        ? currencies.find(i => i.key === milestone.part.currency)
        : currencies.find(i => i.value === 1)
      return {
        ...milestone,
        currency,
        partAmountCents: milestone.applicationMilestone.partAmountCents
      }
    }),
    workflowSteps
  }
}

export const getPaymentMilestones = async (paymentApplications, contract) => {
  const apiMilestones = await store.dispatch(actions.callApi, { url: `contracts/${contract.id}/payments/milestones` })
  const milestones = apiMilestones.map((i) => prepareMilestoneFromApi(paymentApplications, i, contract.currencies))
  return milestones
}

export const addPaymentMilestone = async (contractId, data) => {
  const milestone = await store.dispatch(
    actions.callApi,
    {
      url: `contracts/${contractId}/payments/milestones`,
      method: 'POST',
      body: {
        name: data.name,
        description: data.description,
        paymentAmountCents: data.paymentAmountCents,
        originalDate: data.originalDate,
        forecastedDate: data.forecastedDate,
        expectedInvoiceDate: data.expectedInvoiceDate,
        expectedPaymentDate: data.expectedPaymentDate,
        comment: data.comment,
        purchaseOrder: data.purchaseOrder,
        partId: data.partId
      }
    }
  )
  store.dispatch(actions.createMessage, { text: 'Payment milestone created', color: 'success' })
  return milestone
}

export const saveSubMilestones = async (contractId, milestone, data) => {
  const prepareMilestone = (item) => ({
    name: item.name,
    description: item.description,
    paymentAmountCents: item.paymentAmountCents,
    originalDate: item.originalDate,
    forecastedDate: item.forecastedDate,
    expectedInvoiceDate: item.expectedInvoiceDate,
    expectedPaymentDate: item.expectedPaymentDate,
    comment: item.comment,
    purchaseOrder: item.purchaseOrder,
    id: item.id,
    partId: item.part ? item.part.id : milestone.partId
  })
  const newMilestones = data.filter(item => !item.parentMilestoneId || !item.id).map(item => prepareMilestone({ ...item, id: undefined }))
  const oldMilestones = data.filter(item => item.parentMilestoneId && item.id).map(item => prepareMilestone(item))

  oldMilestones.length && await store.dispatch(
    actions.callApi,
    {
      url: `contracts/${contractId}/payments/milestones/${milestone.id}/subMilestones`,
      method: 'PUT',
      body: oldMilestones
    }
  )

  newMilestones.length && await store.dispatch(
    actions.callApi,
    {
      url: `contracts/${contractId}/payments/milestones/${milestone.id}/subMilestones`,
      method: 'POST',
      body: newMilestones
    }
  )

  store.dispatch(actions.createMessage, { text: 'Parts of milestone saved', color: 'success' })
}

export const updatePaymentMilestone = async (data) => {
  const milestone = await store.dispatch(
    actions.callApi,
    {
      url: `contracts/${data.contractId}/payments/milestones/${data.id}`,
      method: 'PUT',
      body: {
        name: data.name,
        description: data.description,
        paymentAmountCents: isNaN(data.paymentAmountCents) ? null : data.paymentAmountCents,
        originalDate: data.originalDate,
        forecastedDate: data.forecastedDate,
        expectedInvoiceDate: data.expectedInvoiceDate,
        expectedPaymentDate: data.expectedPaymentDate,
        comment: data.comment,
        purchaseOrder: data.purchaseOrder,
        partId: data.partId
      }
    }
  )
  store.dispatch(actions.createMessage, { text: 'Milestone updated', color: 'success' })
  return milestone
}

export const updatePaymentSubMilestone = async (data) => {
  await store.dispatch(
    actions.callApi,
    {
      url: `contracts/${data.contractId}/payments/milestones/${data.parentMilestoneId}/subMilestones/${data.id}`,
      method: 'PUT',
      body: {
        name: data.name,
        description: data.description,
        paymentAmountCents: data.paymentAmountCents,
        originalDate: data.originalDate,
        forecastedDate: data.forecastedDate,
        expectedInvoiceDate: data.expectedInvoiceDate,
        expectedPaymentDate: data.expectedPaymentDate,
        comment: data.comment,
        purchaseOrder: data.purchaseOrder,
        partId: data.partId
      }
    }
  )
  store.dispatch(actions.createMessage, { text: 'Milestone updated', color: 'success' })
}

export const deletePaymentMilestone = async (data) => {
  await store.dispatch(
    actions.callApi,
    {
      url: `contracts/${data.contractId}/payments/milestones/${data.id}`,
      method: 'DELETE'
    }
  )
  store.dispatch(actions.createMessage, { text: 'Milestone deleted', color: 'success' })
}

export const deletePaymentSubMilestone = async (data) => {
  await store.dispatch(
    actions.callApi,
    {
      url: `contracts/${data.contractId}/payments/milestones/${data.parentMilestoneId}/subMilestones/${data.id}`,
      method: 'DELETE'
    }
  )
  store.dispatch(actions.createMessage, { text: 'Part of milestone deleted', color: 'success' })
}

export const getPaymentApplications = async (contract) => {
  const applications = await store.dispatch(actions.callApi, { url: `contracts/${contract.id}/payments/applications` })
  const data = applications.map(i => prepareApplicationFromApi(i, contract.currencies))

  return data
}

export const addPaymentApplication = async (contractId, data) => {
  await store.dispatch(
    actions.callApi,
    {
      url: `contracts/${contractId}/payments/applications`,
      method: 'POST',
      body: {
        name: data.name,
        description: data.description,
        amountCents: data.paymentAmountCents,
        applicationDate: data.applicationDate,
        comment: data.comment || '',
        paymentCertificate: data.paymentCertificate,
        invoiceReference: data.invoiceReference,
        paymentMilestones: data.paymentMilestones.map(milestone => ({
          paymentMilestoneId: milestone.id,
          partAmountCents: milestone.partAmountCents
        })),
        workflowId: data.workflow.id,
        workflowActions: []
      }
    }
  )
  store.dispatch(actions.createMessage, { text: 'Payment application created', color: 'success' })
}

export const updatePaymentApplication = async (data) => {
  await store.dispatch(
    actions.callApi,
    {
      url: `contracts/${data.contractId}/payments/applications/${data.id}`,
      method: 'PUT',
      body: {
        name: data.name,
        description: data.description,
        amountCents: data.paymentAmountCents,
        applicationDate: data.applicationDate,
        comment: data.comment || '',
        paymentCertificate: data.paymentCertificate,
        invoiceReference: data.invoiceReference,
        paymentMilestones: data.paymentMilestones.map(milestone => ({
          paymentMilestoneId: milestone.id,
          partAmountCents: milestone.partAmountCents
        })),
        workflowId: data.workflow.id,
        workflowActions: data.workflowActions
      }
    }
  )
  store.dispatch(actions.createMessage, { text: 'Payment application updated', color: 'success' })
}

export const deletePaymentApplication = async (data) => {
  await store.dispatch(
    actions.callApi,
    {
      url: `contracts/${data.contractId}/payments/applications/${data.id}`,
      method: 'DELETE'
    }
  )
  store.dispatch(actions.createMessage, { text: 'Payment application deleted', color: 'success' })
}

export const addDocumentToPayment = async (data, contractId, paymentId) => {
  await store.dispatch(actions.callApi, {
    url: `contracts/${contractId}/payments/applications/${paymentId}/documents`,
    method: 'post',
    body: data
  })
  store.dispatch(actions.createMessage, { text: 'Document added', color: 'success' })
}

export const calculateMaxAmountsForPaymentForm = (milestones, contractPrices, currencies, parts) => {
  const coveredAmount = getSumOfPrices(milestones.filter(i => !i.variationId && !i.liquidatedDamageId), 'paymentAmountCents', currencies)
  const maxAmountsPerCurrency = Object.keys(contractPrices)
    .reduce((acc, key) => ({ ...acc, [key]: contractPrices[key] - (coveredAmount[key] || 0) }), {})
  const partsPrices = parts.reduce((acc, part) => ({ ...acc, [part.id]: part.signedPriceCents }), {})
  const maxAmountsPerPart = parts.reduce((acc, part) => {
    const milestonesForPart = milestones.filter(i => i.partId === part.id && !i.variationId && !i.liquidatedDamageId).reduce((sum, milestone) => sum + milestone.paymentAmountCents, 0) +
      milestones.filter(i => i.partId === null && !i.variationId && !i.liquidatedDamageId).reduce((sum, milestone) => {
        if (milestone.multiCurrencyMilestone) {
          const value = milestone.subMilestones.filter(i => i.part.id === part.id).reduce((sum, subMilestone) => sum + subMilestone.paymentAmountCents, 0)
          return sum + value
        }
        return sum + ((milestone.paymentAmountCents.sum || milestone.paymentAmountCents) * (part.ratio / 100))
      }, 0)
    acc[part.id] -= milestonesForPart
    return acc
  }, partsPrices)
  return { maxAmountsPerCurrency, maxAmountsPerPart }
}

export const checkMilestonesNamesDuplicates = (milestone, milestonesList, maxOccurrences = 1, includeSubMilestones = true) => {
  const namesList = milestonesList.reduce((acc, curr) => [
    ...acc,
    curr.name,
    ...(includeSubMilestones ? curr.subMilestones.map(i => i.name) : [])
  ], [])
  return namesList.filter(i => i === milestone.name).length >= maxOccurrences
}

export const exportScheduleToExcel = (milestones, contractName) => {
  const getMilestoneExportData = milestone => {
    const {
      parentMilestoneId, name, paymentAmountCents, description, originalDate, forecastedDate, expectedInvoiceDate,
      expectedPaymentDate, purchaseOrder, currency, part, comment
    } = milestone

    return {
      [EXCEL_LABELS.MILESTONE_NAME]: parentMilestoneId ? '' : name,
      [EXCEL_LABELS.SUB_MILESTONE_NAME]: parentMilestoneId ? name : '',
      Payment: isNaN(paymentAmountCents) ? paymentAmountCents.sum / 100 : paymentAmountCents / 100,
      Currency: currency.key,
      Description: description,
      [EXCEL_LABELS.ORIGINAL_MILESTONE_DATE]: formatDate(originalDate),
      [EXCEL_LABELS.FORECASTED_MILESTONE_DATE]: formatDate(forecastedDate),
      [EXCEL_LABELS.EXPECTED_INVOICE_DATE]: formatDate(expectedInvoiceDate),
      [EXCEL_LABELS.EXPECTED_PAYMENT_DATE]: formatDate(expectedPaymentDate),
      [EXCEL_LABELS.ORDER_NUMBER]: purchaseOrder,
      Site: part ? part.name : 'All sites',
      Substantiation: comment
    }
  }

  const getColumnsWidth = milestoneData => {
    const keys = Object.keys(milestoneData)
    return keys.map(k => ({ width: k.length + 10 }))
  }

  const dataForExport = []
  const currentDate = dayjs().format('YYYY-MM-DD')

  for (const m of milestones) {
    dataForExport.push(getMilestoneExportData(m))
    if (m.subMilestones) {
      for (const sm of m.subMilestones) {
        dataForExport.push(getMilestoneExportData(sm))
      }
    }
  }
  const wsCols = getColumnsWidth(dataForExport[0])
  const workbook = XLSX.utils.book_new()
  const worksheet = XLSX.utils.json_to_sheet(dataForExport)
  worksheet['!cols'] = wsCols
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Payment Milestones')
  XLSX.writeFile(workbook, `milestones_${contractName}_${currentDate}.xlsx`)
}
